import 'vite/modulepreload-polyfill';
import React, { useState } from 'react';

interface ParagraphProps {
  text: string | string[] | React.ReactNode;
  type?: string;
  htmlTag?: 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div' | 'span';
  maxChars?: number;
  shouldTruncateText?: boolean;
  viewMoreText?: string;
  viewLessText?: string;
  readMorePosition?: 'inline' | 'below';
  classes?: string;
  style?: React.CSSProperties;
  enableRteStyles?: boolean;
}

export const Paragraph: React.FC<ParagraphProps> = ({
  text,
  type = '',
  htmlTag = 'p',
  maxChars = 5000,
  shouldTruncateText = false,
  viewMoreText = 'Read More',
  viewLessText = 'Read Less',
  readMorePosition = 'inline', // 'inline' or 'below'
  classes = '',
  style = {},
  enableRteStyles = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Helper function to strip HTML tags for accurate character counting
  const stripHtmlTags = (str: string) => str.replace(/<[^>]*>/g, '');

  const shouldTruncate = shouldTruncateText && maxChars > 0 && typeof text === 'string' && stripHtmlTags(text).length > maxChars;

  const getTruncatedText = (str: string, maxChars: number) => {
    const plainText = stripHtmlTags(str);
    if (plainText.length <= maxChars) return str;

    let charCount = 0;
    let result = '';
    let inTag = false;

    for (let i = 0; i < str.length; i++) {
      const char = str[i];

      if (char === '<') {
        inTag = true;
      }

      result += char;

      if (!inTag) {
        charCount++;
        if (charCount >= maxChars) {
          return result + '...';
        }
      }

      if (char === '>') {
        inTag = false;
      }
    }

    return result;
  };

  const truncatedText = shouldTruncate ? getTruncatedText(text as string, maxChars) : text;

  const renderText = () => {
    // Create the dynamic HTML element based on htmlTag
    const createTag = (content: string, key?: number) => {
      const props = {
        className: `${type} ${classes} ${enableRteStyles ? 'rte' : ''}`.trim(),
        style: style,
        dangerouslySetInnerHTML: { __html: content.replace(/\n/g, '<br />') },
        ...(key !== undefined && { key })
      };

      switch (htmlTag) {
        case 'h1':
          return <h1 {...props} />;
        case 'h2':
          return <h2 {...props} />;
        case 'h3':
          return <h3 {...props} />;
        case 'h4':
          return <h4 {...props} />;
        case 'h5':
          return <h5 {...props} />;
        case 'h6':
          return <h6 {...props} />;
        case 'div':
          return <div {...props} />;
        case 'span':
          return <span {...props} />;
        case 'p':
        default:
          return <p {...props} />;
      }
    };

    const createSimpleTag = (content: any, key?: number) => {
      const baseProps = {
        className: `${type} ${classes} ${enableRteStyles ? 'rte' : ''}`.trim(),
        style: style,
        ...(key !== undefined && { key })
      };

      const props = typeof content === 'string'
        ? { ...baseProps, dangerouslySetInnerHTML: { __html: content.replace(/\n/g, '<br />') } }
        : baseProps;

      switch (htmlTag) {
        case 'h1':
          return typeof content === 'string' ? <h1 {...props} /> : <h1 {...props}>{content}</h1>;
        case 'h2':
          return typeof content === 'string' ? <h2 {...props} /> : <h2 {...props}>{content}</h2>;
        case 'h3':
          return typeof content === 'string' ? <h3 {...props} /> : <h3 {...props}>{content}</h3>;
        case 'h4':
          return typeof content === 'string' ? <h4 {...props} /> : <h4 {...props}>{content}</h4>;
        case 'h5':
          return typeof content === 'string' ? <h5 {...props} /> : <h5 {...props}>{content}</h5>;
        case 'h6':
          return typeof content === 'string' ? <h6 {...props} /> : <h6 {...props}>{content}</h6>;
        case 'div':
          return typeof content === 'string' ? <div {...props} /> : <div {...props}>{content}</div>;
        case 'span':
          return typeof content === 'string' ? <span {...props} /> : <span {...props}>{content}</span>;
        case 'p':
        default:
          return typeof content === 'string' ? <p {...props} /> : <p {...props}>{content}</p>;
      }
    };

    if (typeof text === 'string') {
      // Render as plain text with truncation and expansion
      if (shouldTruncate) {
        const displayText = isExpanded ? text : truncatedText;
        const linkText = isExpanded ? viewLessText : viewMoreText;

        if (readMorePosition === 'inline') {
          // Inline positioning: Read More appears within the same element
          const contentWithLink = `${displayText} <a class="underline cursor-pointer" data-toggle-expand="true">${linkText}</a>`;

          return (
            <div onClick={(e) => {
              const target = e.target as HTMLElement;
              if (target.hasAttribute('data-toggle-expand')) {
                e.preventDefault();
                toggleExpand();
              }
            }}>
              {createSimpleTag(contentWithLink)}
            </div>
          );
        } else {
          // Below positioning: Read More appears as a separate block element
          return (
            <div className={`${classes} ${type}`}>
              {createSimpleTag(displayText)}
              <a
                onClick={toggleExpand}
                className='underline mt-2 cursor-pointer block'
              >
                {linkText}
              </a>
            </div>
          );
        }
      } else {
        return createSimpleTag(text);
      }
    } else if (Array.isArray(text)) {
      // Render each item in the array, checking if it's a valid React element or plain text
      return text.map((item, index) =>
        React.isValidElement(item) ? <React.Fragment key={index}>{item}</React.Fragment> : createSimpleTag(item, index)
      );
    } else {
      // Handle non-string, non-array cases as plain text
      return createSimpleTag(text);
    }
  };

  return <div>{renderText()}</div>;
};

export default Paragraph;
